// Copyright (c) Jonathan Ferraz.
// Licensed under the MIT license.

import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import './styles.scss';

import { CardBlur } from 'components';

import {
  Container,
  Content,
  Footer,
  Grid,
  RowOne,
  RowOneUpper,
  RowThree,
  RowTwo,
  RowTwoUpper,
  Wrapper,
} from './styles';
import emailjs from '@emailjs/browser';
import { ReactComponent as YellowPic } from 'assets/icons/yellow.svg';
import Ipad from 'assets/images/contact/ipad.webp';
import Iphone from 'assets/images/contact/iphone-14.webp';
import YellowMobile from 'assets/images/contact/yellow-mobile.webp';
import useScreenSize from 'hooks/useScreenSize';
import { validateEmail } from 'utils/HTMLUtils';

export const Contact = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [projectDetails, setProjectDetails] = useState<string>('');
  const [deadline, setDeadline] = useState<string>('');
  const [budget, setBudget] = useState<string | undefined>(undefined);

  const screenSize = useScreenSize();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const isMobileQuery = useMemo(() => {
    if (screenSize === 'md' || screenSize === 'sm' || screenSize === 'xs') {
      return true;
    }

    return false;
  }, [screenSize]);

  const form = useRef<any>();
  const sendEmail = (e: any) => {
    e.preventDefault();
    setLoading(true);

    emailjs
      .sendForm(
        'service_zkcz3db',
        'template_2qwdma5',
        form.current,
        'BYE3WUW6Uu6kpD5iE'
      )
      .then(
        () => {
          setLoading(false);
          setName('');
          setEmail('');
          setProjectDetails('');
          setDeadline('');
          setBudget('');
          toast.success('Your data has been sent!', {
            position: toast.POSITION.TOP_RIGHT,
            closeButton: false,
            className: 'toast-message',
          });
        },
        () => {
          setLoading(false);
          toast.error('There was an error sending the data!', {
            position: toast.POSITION.TOP_RIGHT,
            closeButton: false,
            className: 'toast-message',
          });
        }
      );
  };

  const disableButton = useMemo(() => {
    if (
      !name ||
      !email ||
      !projectDetails ||
      !deadline ||
      !budget ||
      !validateEmail(email)
    ) {
      return true;
    }

    return false;
  }, [name, email, projectDetails, deadline, budget]);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      const element = textareaRef.current;
      element.style.height = 'auto';
      element.style.height = `${element.scrollHeight}px`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setProjectDetails(e.target.value);
    adjustTextareaHeight();
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [projectDetails]);

  const formatCurrency = (input: string): string => {
    const value = parseFloat(input.replace(/[^\d]/g, ''));

    return value
      .toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
      })
      .replace(/[.,]/g, '');
  };

  const handleInputChange2 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const formattedValue = formatCurrency(rawValue);

    if (
      rawValue === '' ||
      rawValue === '€ ' ||
      rawValue === '€' ||
      rawValue === '€ NaN'
    ) {
      setBudget(undefined);
    } else {
      setBudget(formattedValue);
    }
  };

  return (
    <Container id={'contact'}>
      <Wrapper>
        {isMobileQuery ? <img src={YellowMobile} alt='' /> : <YellowPic />}

        <Grid>
          <RowOneUpper>
            <Content>
              <form ref={form} onSubmit={sendEmail}>
                <input
                  type={'text'}
                  value={name}
                  name={'brand_name'}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setName(e.target.value)
                  }
                  placeholder={'Name'}
                  required
                />
                <input
                  type={'email'}
                  name={'user_email'}
                  value={email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setEmail(e.target.value)
                  }
                  placeholder={'E-mail address'}
                  required
                />
                <textarea
                  ref={textareaRef}
                  value={projectDetails}
                  name={'project_details'}
                  onChange={handleInputChange}
                  placeholder={'What is the project about?'}
                  rows={1}
                  style={{
                    verticalAlign: 'middle',
                    resize: 'none',
                    overflowY: 'hidden',
                  }}
                  required
                />
                <input
                  name={'deadline_input'}
                  type={'text'}
                  value={deadline}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDeadline(e.target.value)
                  }
                  placeholder={'Deadline'}
                  required
                />
                <input
                  name={'estimated_budget'}
                  type={'text'}
                  value={budget || ''}
                  onChange={handleInputChange2}
                  onKeyPress={(event) => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  placeholder={'Estimated Budget'}
                  required
                />
                <Footer>
                  <button
                    type={'submit'}
                    value={'Send'}
                    disabled={disableButton || loading}
                    tabIndex={disableButton ? -1 : 0}
                    aria-disabled={disableButton ? 'true' : 'false'}
                    onClick={sendEmail}
                  >
                    <span>Send request</span>
                  </button>
                </Footer>
              </form>
            </Content>
          </RowOneUpper>
          <RowOne>
            <CardBlur
              fullWidth
              bgColor={'rgba(13, 13, 13, 0.90)'}
              borderColor={'rgba(252, 222, 134, 0.15)'}
              borderRadius={isMobileQuery ? 10 : 30}
            >
              <div />
            </CardBlur>
          </RowOne>
          <RowTwo>
            <CardBlur
              fullWidth
              bgColor={'rgba(13, 13, 13, 0.90)'}
              borderColor={'rgba(252, 222, 134, 0.15)'}
              borderRadius={isMobileQuery ? 10 : 30}
            >
              <div />
            </CardBlur>
          </RowTwo>
          <RowTwoUpper>
            <div>
              <h1>Responsive UI Design</h1>
              <figure>
                <img src={Iphone} alt={'t-shirt'} />
              </figure>
            </div>
          </RowTwoUpper>
          <RowThree>
            <CardBlur
              fullWidth
              bgColor={'rgba(13, 13, 13, 0.90)'}
              borderColor={'rgba(252, 222, 134, 0.15)'}
              borderRadius={isMobileQuery ? 10 : 30}
            >
              <div>
                <h1>
                  Product Design <strong>& 3D Renders</strong>
                </h1>
                <figure>
                  <img src={Ipad} alt={'t-shirt'} />
                </figure>
              </div>
            </CardBlur>
          </RowThree>
        </Grid>
      </Wrapper>
    </Container>
  );
};
